import { zodResolver } from '@hookform/resolvers/zod';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';

import {
  ActionBtnStyled,
  AttachmentErrorStyled,
  AttachmentFormInputStyled,
  AttachmentNameStyled,
  AttachmentPhotoWrapStyled,
  AttachmentWrapStyled,
  FlexContainerControlsStyled,
  FlexContainerMessageStyled,
  FormElementWrapStyled,
  FormStyled,
  IconWrapStyled,
  TypographyTitleStyled,
  getPopupContentStyle,
} from './AddAttachmentPopup.styled';
import { TicketAttachmentFormValuesT } from './declarations';
import useAddAttachmentPopup from './hooks/useAddAttachmentPopup';
import validationSchema from './validationSchema';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import { FormInput, FormSelect } from 'components/Form';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { NoopT } from 'declarations/types/common';
import { AttachFileIcon, PhotoCameraIcon } from 'icons';

export type AddAttachmentPopupPropsT = {
  ticketId: string;
  categoryName: string;
  isOpen: boolean;
  assignToWarrCloud: boolean;
  onClose: NoopT;
};

const AddAttachmentPopup: FC<AddAttachmentPopupPropsT> = ({
  ticketId,
  categoryName,
  isOpen,
  assignToWarrCloud,
  onClose,
}) => {
  /**
   * Hooks
   */
  const fileInput = useRef<HTMLInputElement | null>(null);
  const { isMobile } = useResponsive();
  const {
    handleSubmitForm,
    isCreatingAttachment,
    isAttachmentCreationError,
    createAttachmentErrorMessage,
    createAttachmentValidationErrorMessage,
    isAttachmentSuccessfullyPublished,
    showAttachmentsForm,
    handleClosePopup,
    attachmentTypesOptions,
    isLoadingAttachmentTypesOptions,
  } = useAddAttachmentPopup({ ticketId, categoryName, assignToWarrCloud });

  const fromMethods = useForm<TicketAttachmentFormValuesT>({
    defaultValues: {
      note: '',
      attachmentTypeCode: '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  /**
   * Variables
   */
  const { control, handleSubmit, reset } = fromMethods;

  // Register file fields so setValue/trigger work immediately
  useEffect(() => {
    fromMethods.register('attachmentFile');
    fromMethods.register('attachmentPhoto');
  }, [fromMethods]);

  // Watch file inputs
  const attachmentFileValue = useWatch<TicketAttachmentFormValuesT, 'attachmentFile'>({
    control,
    name: 'attachmentFile',
  });
  const attachmentPhotoValue = useWatch<TicketAttachmentFormValuesT, 'attachmentPhoto'>({
    control,
    name: 'attachmentPhoto',
  });

  const attachmentFileNames = attachmentFileValue
    ? Array.from(attachmentFileValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentPhotoNames = attachmentPhotoValue
    ? Array.from(attachmentPhotoValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentNames = [attachmentFileNames, attachmentPhotoNames].filter(Boolean).join(', ');

  // Original combined list
  const attachmentValues = useMemo(
    () =>
      [...Array.from(attachmentFileValue || []), ...Array.from(attachmentPhotoValue || [])].filter(
        Boolean
      ),
    [attachmentFileValue, attachmentPhotoValue]
  );

  // parallel mapping to know where each item came from (for mobile)
  const attachmentValueSources = useMemo(() => {
    const files = Array.from(attachmentFileValue || []).map((_, i) => ({
      src: 'file' as const,
      idx: i,
    }));
    const photos = Array.from(attachmentPhotoValue || []).map((_, i) => ({
      src: 'photo' as const,
      idx: i,
    }));
    return [...files, ...photos];
  }, [attachmentFileValue, attachmentPhotoValue]);

  /**
   * Validation-aware helpers
   */
  type FileSourceT = 'file' | 'photo';

  const toFileCollection = (files: File[]): FileList | File[] => {
    try {
      if (typeof window !== 'undefined' && 'DataTransfer' in window) {
        const dt = new DataTransfer();
        files.forEach(f => dt.items.add(f));
        return dt.files;
      }
    } catch {}
    return files as any;
  };

  const setFilesOnField = (name: FileSourceT, files: File[]) => {
    fromMethods.setValue(
      name === 'file' ? 'attachmentFile' : 'attachmentPhoto',
      toFileCollection(files) as any,
      { shouldDirty: true, shouldTouch: true, shouldValidate: true }
    );
    fromMethods.trigger(['attachmentFile', 'attachmentPhoto']);
  };

  const pushFilesToField = (name: FileSourceT, incoming: FileList | File[]) => {
    const current = name === 'file' ? attachmentFileValue : attachmentPhotoValue;
    const merged = [...Array.from(current || []), ...Array.from(incoming || [])];
    setFilesOnField(name, merged);
  };

  const removeOne = (name: FileSourceT, idx: number) => {
    const current = name === 'file' ? attachmentFileValue : attachmentPhotoValue;
    const next = Array.from(current || []).filter((_, i) => i !== idx);
    setFilesOnField(name, next);
    if (fileInput.current) fileInput.current.value = '';
  };

  /**
   * Handlers
   */
  const handleDropZoneClick = () => {
    fileInput.current?.click();
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      pushFilesToField('file', files);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      pushFilesToField('file', files);
    }
  };

  /**
   * Effects
   */
  useEffect(
    () => () => {
      if (isOpen) {
        reset();
      }
    },
    [isOpen, reset]
  );

  const { errors, touchedFields, dirtyFields, submitCount } = useFormState({ control });

  const rootError = (errors as any)?.root?.message as string | undefined;
  const attachmentErrorMessage =
    (errors.attachmentFile?.message as string | undefined) ||
    (errors.attachmentPhoto?.message as string | undefined) ||
    rootError;

  const showAttachmentError =
    !!attachmentErrorMessage &&
    (submitCount > 0 ||
      touchedFields.attachmentFile ||
      touchedFields.attachmentPhoto ||
      dirtyFields.attachmentFile ||
      dirtyFields.attachmentPhoto);

  return (
    <Popup
      nested
      popupId={ScrollLockIdE.AddAttachmentPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isOpen}
      onClose={handleClosePopup(onClose)}
      showCloseBtn={isAttachmentSuccessfullyPublished || isAttachmentCreationError}
      closeOnEscape={false}
    >
      {showAttachmentsForm && !isAttachmentCreationError && (
        <>
          <TypographyTitleStyled variant="h2" color="dark" align="center">
            Publish Attachment
          </TypographyTitleStyled>
          <FormProvider {...fromMethods}>
            <FormStyled role="form" onSubmit={handleSubmit(handleSubmitForm)}>
              {!isMobile && (
                <Box px={4} pt={0} pb={6}>
                  <Stack
                    onClick={handleDropZoneClick}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    direction="column"
                    justifyContent="center"
                    spacing={4}
                    sx={{
                      marginTop: attachmentValues.length > 0 ? '30px' : '10px',
                      height: '200px',
                      borderWidth: '1px',
                      borderStyle: 'dashed',
                      borderColor: grey['500'],
                      borderRadius: 2,
                      p: '10px',
                      cursor: 'pointer',
                    }}
                  >
                    {attachmentValues.length <= 0 && (
                      <Box id="upload-container" textAlign="center">
                        <CloudUploadOutlinedIcon
                          sx={{
                            fontSize: '50px',
                            ':hover': { color: grey['900'], cursor: 'pointer' },
                          }}
                        />
                        <Stack direction="column" justifyContent="center" alignItems="center">
                          <Typography>Select a file or drag and drop here</Typography>
                          <Typography fontSize={12} color={grey['500']}>
                            Size no more than 10MB
                          </Typography>
                        </Stack>
                        <input
                          style={{ display: 'none' }}
                          type="file"
                          name="attachmentFile"
                          placeholder="Attachment File"
                          multiple
                          ref={fileInput}
                          onChange={handleFileChange}
                        />
                      </Box>
                    )}

                    {attachmentValues.length > 0 && (
                      <Box>
                        <List dense sx={{ maxHeight: '270px', overflowY: 'auto' }}>
                          {attachmentValues.map((file, i) => (
                            <ListItem
                              key={`${attachmentValueSources[i]?.src}-${attachmentValueSources[i]?.idx}-${file.name}`}
                              secondaryAction={
                                <IconButton
                                  aria-label="remove file"
                                  edge="end"
                                  onClick={e => {
                                    e.stopPropagation();
                                    const meta = attachmentValueSources[i];
                                    if (meta) removeOne(meta.src, meta.idx);
                                  }}
                                >
                                  <CloseRoundedIcon />
                                </IconButton>
                              }
                            >
                              <FileIcon />
                              <ListItemText primary={file.name} sx={{ marginLeft: '10px' }} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}

              <FormElementWrapStyled>
                <FormInput
                  name="note"
                  label="Enter Attachment Note"
                  placeholder="Attachment Note"
                />
              </FormElementWrapStyled>

              <label htmlFor="attachmentTypeCode" hidden>
                attachment type
              </label>
              <FormElementWrapStyled>
                <FormSelect
                  name="attachmentTypeCode"
                  placeholder="Enter Type"
                  options={attachmentTypesOptions}
                  isLoading={isLoadingAttachmentTypesOptions}
                  isCompactMenuMode={!isMobile}
                />
              </FormElementWrapStyled>

              <AttachmentWrapStyled justify="flex-start" align="start">
                {isMobile && (
                  <>
                    <AttachmentWrapStyled justify="flex-start" align="start">
                      <AttachmentFormInputStyled
                        name="attachmentFile"
                        placeholder="Attachment File"
                        type="file"
                        multiple
                        label={
                          <IconWrapStyled justify="center" title="Choose File">
                            <AttachFileIcon
                              size={isMobile ? '3xl' : '2xl'}
                              color={attachmentFileNames ? 'green' : 'darkBlue'}
                            />
                          </IconWrapStyled>
                        }
                      />
                    </AttachmentWrapStyled>

                    <AttachmentPhotoWrapStyled>
                      <AttachmentFormInputStyled
                        name="attachmentPhoto"
                        placeholder="Attachment Photo"
                        type="file"
                        capture="user"
                        accept="image/*"
                        multiple
                        label={
                          <IconWrapStyled justify="center">
                            <PhotoCameraIcon
                              size={isMobile ? '3xl' : '2xl'}
                              color={attachmentPhotoNames ? 'green' : 'darkBlue'}
                            />
                          </IconWrapStyled>
                        }
                      />
                    </AttachmentPhotoWrapStyled>

                    <AttachmentNameStyled size="md">
                      {attachmentValues.length > 0 && (
                        <Box>
                          <List dense sx={{ maxHeight: '270px', overflowY: 'auto' }}>
                            {attachmentValues.map((file, i) => (
                              <ListItem
                                key={`${attachmentValueSources[i]?.src}-${attachmentValueSources[i]?.idx}-${file.name}`}
                                secondaryAction={
                                  <IconButton
                                    aria-label="remove file"
                                    edge="end"
                                    onClick={e => {
                                      e.stopPropagation();
                                      const meta = attachmentValueSources[i];
                                      if (meta) removeOne(meta.src, meta.idx);
                                    }}
                                  >
                                    <CloseRoundedIcon />
                                  </IconButton>
                                }
                              >
                                <FileIcon />
                                <ListItemText primary={file.name} sx={{ marginLeft: '10px' }} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      {!attachmentValues.length && (attachmentNames || 'Attachment not selected')}
                    </AttachmentNameStyled>
                  </>
                )}
              </AttachmentWrapStyled>

              {showAttachmentError && (
                <AttachmentErrorStyled size={isMobile ? 'md' : 'sm'} color="error">
                  {attachmentErrorMessage}
                </AttachmentErrorStyled>
              )}

              <FlexContainerControlsStyled justify={isMobile ? 'center' : 'flex-end'}>
                <ActionBtnStyled
                  type="button"
                  variant="outline"
                  size="xl"
                  onClick={handleClosePopup(onClose)}
                >
                  Cancel
                </ActionBtnStyled>
                <ActionBtnStyled size="xl" isLoading={isCreatingAttachment}>
                  Publish
                </ActionBtnStyled>
              </FlexContainerControlsStyled>
            </FormStyled>
          </FormProvider>
        </>
      )}

      {isAttachmentSuccessfullyPublished && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Attachment successfully published" />
        </FlexContainerMessageStyled>
      )}

      {isAttachmentCreationError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={createAttachmentErrorMessage || createAttachmentValidationErrorMessage}
          />
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default AddAttachmentPopup;
